import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@lib/api/client';
import { captureScreenMap } from '../lib/captureScreenMap';

export interface SupportStep {
  action: string;
  target?: string;
  label?: string;
}

export interface SupportHelp {
  loading: boolean;
  reply: string | null;
  steps: SupportStep[];
  failed: boolean;
  // Id de la interacción: el panel de feedback ("¿te sirvió?") lo referencia.
  interactionId: string | null;
  // Id de sesión: la guía iterativa lo usa para pedir los próximos pasos.
  sessionId: string | null;
}

interface Params {
  enabled: boolean; // normalmente: el modal está abierto y hay un error
  message: string; // descripción del error / acción (lo que se le manda al agente)
  errorCodes?: string[]; // códigos (ej. DGI) para dar más contexto
  screen?: string; // pantalla/acción donde ocurrió
}

/**
 * Consulta al agente de soporte (vía el proxy /api/support/report) para obtener
 * una explicación + pasos sugeridos ante un error. Se dispara solo cuando cambia
 * el error (no en cada render). La compañía activa se adjunta automáticamente.
 */
export function useSupportHelp({ enabled, message, errorCodes, screen }: Params): SupportHelp {
  // Leemos la compañía del cache (sin disparar fetch ni acoplar auth).
  const queryClient = useQueryClient();
  const [state, setState] = useState<SupportHelp>({
    loading: false,
    reply: null,
    steps: [],
    failed: false,
    interactionId: null,
    sessionId: null,
  });

  // Clave estable: re-consulta solo si cambia el error (no por la compañía/render).
  const key = enabled && message ? `${screen || ''}|${message}|${(errorCodes || []).join(',')}` : '';

  useEffect(() => {
    if (!key) {
      setState({ loading: false, reply: null, steps: [], failed: false, interactionId: null, sessionId: null });
      return;
    }
    let alive = true;
    setState({ loading: true, reply: null, steps: [], failed: false, interactionId: null, sessionId: null });
    const sc: any = (queryClient.getQueryData(['company', 'selected']) as any)?.company;
    apiClient
      .post(
        '/support/report',
        {
          message,
          context: {
            screen,
            action: screen,
            error_codes: errorCodes,
            // Mapa de la pantalla actual: el agente targetea por `ref` exacto.
            screen_elements: captureScreenMap(),
            company_name: sc?.name || sc?.fullName || undefined,
            company_legal_name: sc?.fullName || undefined,
            company_ruc: sc?.ruc || sc?.numberId || undefined,
            company_dv: sc?.dv || undefined,
            company_id: sc?.id || undefined,
          },
        },
        { timeout: 130000 }
      )
      .then(({ data }) => {
        if (alive)
          setState({
            loading: false,
            reply: data?.reply ?? null,
            steps: data?.steps ?? [],
            failed: false,
            interactionId: data?.interaction_id ?? null,
            sessionId: data?.session_id ?? null,
          });
      })
      .catch(() => {
        if (alive) setState({ loading: false, reply: null, steps: [], failed: true, interactionId: null, sessionId: null });
      });
    return () => {
      alive = false;
    };
    // Intencional: dependemos de `key` (el error) para no re-consultar en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
